import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Target, Rocket, Bot, GitBranch, Brain, FileCheck,
  CheckCircle2, ChevronDown, Sparkles,
  Users, BarChart3, Award, TrendingUp,
  Layers, Zap, Globe, Star, Monitor, BookOpen,
  Code2, Trophy, Building2, GraduationCap, BadgeCheck,
  Activity, Cpu, ChevronRight, Layout, Palette,
  UserCheck, Briefcase, MessageSquare, PieChart,
  Lock, Shield, ArrowRight,
} from 'lucide-react';

/* ── Utility: IntersectionObserver hook ─────────────────────── */
function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

/* ── Animated counter ───────────────────────────────────────────── */
function Counter({ target, duration = 1800 }) {
  const [val, setVal] = useState(0);
  const [ref, visible] = useInView(0.3);
  useEffect(() => {
    if (!visible) return;
    const numeric = parseInt(String(target).replace(/\D/g, '')) || 0;
    if (!numeric) { setVal(target); return; }
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const prog = Math.min((ts - start) / duration, 1);
      setVal(Math.floor(prog * numeric));
      if (prog < 1) requestAnimationFrame(step);
      else setVal(target);
    };
    requestAnimationFrame(step);
  }, [visible, target, duration]);
  return <span ref={ref}>{val}</span>;
}

/* ── FadeUp wrapper ─────────────────────────────────────────────── */
function FadeUp({ children, delay = 0, style = {}, className = '' }) {
  const [ref, visible] = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.65s ease ${delay}ms, transform 0.65s ease ${delay}ms`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* ── Section separator ──────────────────────────────────────────── */
function SectionDivider({ light = false }) {
  return (
    <div style={{ width: '100%', height: 1, background: light ? 'rgba(255,255,255,.08)' : 'rgba(99,102,241,.08)' }} />
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
export default function CollegeIntroPage() {
  const navigate = useNavigate();
  const whyRef = useRef(null);

  const scrollToWhy = () =>
    whyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const particles = [
    { top: '10%', left: '5%',   size: 3, dur: '6s',   del: '0s'   },
    { top: '25%', left: '16%',  size: 2, dur: '8s',   del: '1.2s' },
    { top: '52%', left: '3%',   size: 4, dur: '7s',   del: '0.5s' },
    { top: '72%', left: '12%',  size: 2, dur: '9s',   del: '2s'   },
    { top: '8%',  right: '7%',  size: 3, dur: '7.5s', del: '0.8s' },
    { top: '33%', right: '4%',  size: 2, dur: '6.5s', del: '1.8s' },
    { top: '62%', right: '9%',  size: 3, dur: '8.5s', del: '0.3s' },
    { top: '82%', right: '16%', size: 2, dur: '7s',   del: '1.5s' },
    { top: '45%', left: '48%',  size: 2, dur: '10s',  del: '3s'   },
  ];

  return (
    <div style={{ fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif', background: '#F8FAFF', minHeight: '100vh' }}>

      {/* ─── Global Keyframes & Responsive Classes ─── */}
      <style>{`
        @keyframes introFloat {
          0%,100% { transform:translateY(0) scale(1); opacity:.55; }
          50%      { transform:translateY(-14px) scale(1.3); opacity:1; }
        }
        @keyframes introBlob1 {
          0%,100% { transform:translate(0,0) scale(1); }
          40%     { transform:translate(44px,-32px) scale(1.08); }
          70%     { transform:translate(-22px,30px) scale(.95); }
        }
        @keyframes introBlob2 {
          0%,100% { transform:translate(0,0) scale(1); }
          35%     { transform:translate(-38px,28px) scale(1.06); }
          68%     { transform:translate(24px,-22px) scale(.94); }
        }
        @keyframes introBlob3 {
          0%,100% { transform:translate(0,0) scale(1); }
          50%     { transform:translate(30px,22px) scale(1.05); }
        }
        @keyframes shimmer {
          0%   { background-position:200% 0; }
          100% { background-position:-200% 0; }
        }
        @keyframes roadmapGlow {
          0%,100% { box-shadow: 0 0 8px rgba(196,181,253,.4), 0 0 24px rgba(139,92,246,.2); }
          50%      { box-shadow: 0 0 16px rgba(196,181,253,.8), 0 0 48px rgba(139,92,246,.4); }
        }
        @keyframes pulse {
          0%,100% { transform:scale(1); opacity:1; }
          50%      { transform:scale(1.12); opacity:.8; }
        }

        /* ─── Global Layout ─── */
        :root {
          --primary-gradient: linear-gradient(135deg, #312E81, #5B21B6, #7C3AED);
          --secondary-accent: #8B5CF6;
          --cta-gradient: linear-gradient(135deg, #6366F1, #8B5CF6);
          --highlight-gradient: linear-gradient(135deg, #7C3AED, #A855F7);
          --hero-bg: linear-gradient(135deg, #0F172A, #1E1B4B, #312E81);
        }
        .ip-section-wrapper {
          width: 100%;
          display: flex;
          justify-content: center;
          padding-top: 110px;
          padding-bottom: 110px;
          position: relative;
        }
        .ip-container {
          width: min(95vw, 1700px);
          max-width: 1700px;
          margin: auto;
          padding-left: clamp(40px, 5vw, 90px);
          padding-right: clamp(40px, 5vw, 90px);
        }
        @media (max-width: 1024px) {
          .ip-container { padding-left: 32px; padding-right: 32px; }
        }
        @media (max-width: 768px) {
          .ip-container { padding-left: 20px; padding-right: 20px; }
        }

        .ip-btn-primary {
          display:inline-flex; align-items:center; gap:9px;
          padding:16px 36px; border-radius:16px; border:none; cursor:pointer;
          font-size:16px; font-weight:800; color:#fff; letter-spacing:.01em;
          background: var(--cta-gradient);
          box-shadow:0 8px 28px rgba(99,102,241,.38);
          transition:transform .25s ease,box-shadow .25s ease;
          position:relative; overflow:hidden;
        }
        .ip-btn-primary::after {
          content:''; position:absolute; inset:0;
          background:linear-gradient(105deg,transparent 35%,rgba(255,255,255,.32) 50%,transparent 65%);
          background-size:200% 100%; animation:shimmer 2.4s linear infinite;
        }
        .ip-btn-primary:hover { transform:translateY(-3px); box-shadow:0 16px 40px rgba(99,102,241,.48); }
        .ip-btn-secondary {
          display:inline-flex; align-items:center; gap:9px;
          padding:16px 32px; border-radius:16px; cursor:pointer;
          font-size:16px; font-weight:700; color:rgba(255,255,255,.92);
          background:rgba(255,255,255,.10); border:1.5px solid rgba(255,255,255,.28);
          backdrop-filter:blur(8px); -webkit-backdrop-filter:blur(8px);
          transition:background .25s ease,transform .25s ease;
        }
        .ip-btn-secondary:hover { background:rgba(255,255,255,.18); transform:translateY(-3px); }
        
        .ip-card {
          transition: transform .3s ease, box-shadow .3s ease, border-color .3s ease;
          padding: 36px !important;
          border-radius: 24px !important;
          background: #fff;
          border: 1px solid rgba(99,102,241,0.08);
          box-shadow: 0 8px 32px rgba(99,102,241,0.08);
          display: flex;
          flex-direction: column;
        }
        .ip-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 24px 60px rgba(99,102,241,0.16) !important;
          border-color: rgba(99,102,241,0.15) !important;
        }

        .ip-card-glass {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 24px;
          padding: 32px;
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
        }
        .ip-card-glass:hover {
          transform: translateY(-6px);
          background: rgba(255, 255, 255, 0.09);
          box-shadow: 0 28px 64px rgba(0, 0, 0, 0.28);
          border-color: rgba(255, 255, 255, 0.18);
        }
        
        .timeline-row {
          display:grid; grid-template-columns:1fr 72px 1fr; align-items:center; margin-bottom:40px;
        }
        .tl-card-left { display:flex; justify-content:flex-end; padding-right:40px; }
        .tl-card-right { display:flex; justify-content:flex-start; padding-left:40px; }
        .tl-empty { display:block; }
        @media (max-width:680px) {
          .timeline-row {
            grid-template-columns:56px 1fr; gap:0; margin-bottom:24px;
          }
          .tl-card-left,.tl-card-right { justify-content:flex-start; padding:0 0 0 0; display:block; }
          .tl-empty { display:none; }
          .tl-dot-wrap { justify-content:center; }
        }
        .journey-row { display:flex; flex-wrap:nowrap; align-items:stretch; overflow-x:auto; padding-bottom:8px; gap:0; }
        @media (max-width:900px) { .journey-row { flex-wrap:wrap; gap:12px; justify-content:center; } }
        .trust-badges { display:flex; flex-wrap:wrap; gap:20px; justify-content:center; }
        .intro-two-col {
          display:grid;
          grid-template-columns: 55% 45%;
          gap: 72px;
          align-items: start;
          width: 100%;
        }
        @media (max-width:1100px) { .intro-two-col { grid-template-columns:1fr; gap:48px; } }
        .wl-cards { display:flex; gap:28px; justify-content: space-between; align-items: stretch; }
        .wl-card-item { width: 48%; }
        @media (max-width:850px) { .wl-cards { flex-direction: column; } .wl-card-item { width: 100%; } }

        .portal-feature-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          width: 100%;
        }
        @media (max-width: 640px) {
          .portal-feature-grid { grid-template-columns: 1fr; }
        }

        .portal-feature-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 18px;
          border-radius: 16px;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          height: 100%;
          min-height: 64px;
        }
        .portal-feature-card:hover {
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(255, 255, 255, 0.2);
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
        }
        .portal-icon-box {
          width: 36px;
          height: 36px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.05);
        }
        .portal-feature-label {
          font-size: 13.5px;
          color: rgba(226, 232, 240, 0.95);
          font-weight: 600;
          line-height: 1.4;
        }
        
        .feature-grid {
          display: grid;
          gap: 28px;
          grid-template-columns: repeat(4, 1fr);
          align-items: stretch;
        }
        @media (max-width: 1200px) { .feature-grid { grid-template-columns: repeat(3, 1fr); } }
        @media (max-width: 1024px) { .feature-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 640px)  { .feature-grid { grid-template-columns: 1fr; } }

        .benefits-grid {
          display: grid;
          gap: 28px;
          grid-template-columns: repeat(4, 1fr);
          align-items: stretch;
        }
        @media (max-width: 1024px) { .benefits-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 640px)  { .benefits-grid { grid-template-columns: 1fr; } }

        .comparison-grid {
          display: flex;
          gap: 40px;
          justify-content: space-between;
        }
        .comparison-card { width: 48%; }
        @media (max-width: 850px) { .comparison-grid { flex-direction: column; } .comparison-card { width: 100%; } }

        h1 { font-size: clamp(28px, 4.5vw, 68px) !important; font-weight: 800 !important; line-height: 1.1 !important; max-width: none !important; }
        h2 { font-size: clamp(22px, 3.5vw, 52px) !important; font-weight: 700 !important; line-height: 1.2 !important; max-width: none !important; }
        p { font-size: clamp(14px, 1.2vw, 18px) !important; line-height: 1.75 !important; max-width: 780px !important; margin-left: auto !important; margin-right: auto !important; color: #475569; }

        .premium-gradient-text {
          background: var(--highlight-gradient);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .premium-chip {
          padding: 8px 20px;
          border-radius: 99px;
          background: rgba(99, 102, 241, 0.05);
          border: 1px solid rgba(99, 102, 241, 0.12);
          font-size: 14px;
          font-weight: 600;
          color: #4F46E5;
          transition: all 0.25s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .premium-chip:hover {
          background: rgba(99, 102, 241, 0.1);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.08);
        }

        @keyframes float-dashboard {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }
        .floating-dashboard {
          animation: float-dashboard 6s ease-in-out infinite;
          margin-top: 0;
        }
        /* ── Journey Section ── */
        @keyframes journeyIconFloat {
          0%,100% { transform: translateY(0px) scale(1); }
          50%      { transform: translateY(-6px) scale(1.06); }
        }
        @keyframes journeyBadgePulse {
          0%,100% { box-shadow: 0 0 0 0 rgba(99,102,241,0.4); }
          50%      { box-shadow: 0 0 0 6px rgba(99,102,241,0); }
        }
        @keyframes journeyLineGlow {
          0%,100% { opacity: 0.5; }
          50%      { opacity: 1; }
        }
        .journey-card {
          background: #fff;
          border-radius: 22px;
          border: 1px solid rgba(99,102,241,0.1);
          box-shadow: 0 8px 32px rgba(99,102,241,0.08);
          padding: 22px 14px 18px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
          cursor: default;
          position: relative;
          overflow: hidden;
          height: 100%;
          box-sizing: border-box;
        }
        .journey-card::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 22px;
          opacity: 0;
          background: linear-gradient(160deg, rgba(139,92,246,0.04) 0%, transparent 60%);
          transition: opacity 0.3s ease;
        }
        .journey-card:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 20px 56px rgba(99,102,241,0.18);
        }
        .journey-card:hover::before { opacity: 1; }
        .journey-grid {
          display: grid;
          grid-template-columns: repeat(9, 1fr);
          gap: 14px;
          align-items: stretch;
        }
        @media (max-width: 1280px) {
          .journey-grid { grid-template-columns: repeat(5, 1fr); }
        }
        @media (max-width: 900px) {
          .journey-grid { grid-template-columns: repeat(3, 1fr); gap: 16px; }
        }
        @media (max-width: 560px) {
          .journey-grid { grid-template-columns: 1fr; max-width: 340px; margin: 0 auto; }
        }
        .journey-section-h2 {
          font-size: 52px !important;
          font-weight: 800 !important;
          line-height: 1.15 !important;
          max-width: 780px !important;
          margin: 0 auto 24px !important;
          text-align: center !important;
          color: #0F172A !important;
        }
        @media (max-width: 1024px) { .journey-section-h2 { font-size: 40px !important; } }
        @media (max-width: 640px)  { .journey-section-h2 { font-size: 30px !important; } }

        @keyframes logoFloat {
          0%,100% { transform: translateY(0px); }
          50%      { transform: translateY(-10px); }
        }
        @keyframes logoGlow {
          0%,100% { opacity: 0.4; transform: scale(1); }
          50%      { opacity: 0.7; transform: scale(1.08); }
        }
        .intro-hero-h1 {
          font-size: clamp(38px,4vw,58px) !important;
          font-weight: 800 !important;
          line-height: 1.05 !important;
          max-width: none !important;
          margin-bottom: 24px !important;
          text-align: left !important;
          margin-left: 0 !important;
          color: #0F172A !important;
          letter-spacing: -0.025em !important;
        }
        @media (max-width: 900px) { .intro-hero-h1 { font-size: clamp(26px,5vw,38px) !important; text-align:center !important; margin:0 auto 24px !important; } }
        .intro-hero-p {
          font-size: 20px !important;
          line-height: 1.9 !important;
          max-width: 760px !important;
          margin-left: 0 !important;
          margin-right: 0 !important;
          color: #475569 !important;
          text-align: left !important;
        }
        @media (max-width: 900px) { .intro-hero-p { text-align:center !important; margin-left:auto !important; margin-right:auto !important; } }

        /* ── Hero feature chips — 4-per-row CSS grid ── */
        .hero-chips-container {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 14px;
          width: 100%;
          margin-bottom: 40px;
        }
        @media (max-width: 768px) { .hero-chips-container { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 480px) { .hero-chips-container { grid-template-columns: 1fr; } }
        .hero-chip {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          height: 48px; border-radius: 999px; cursor: default;
          font-size: 14px; font-weight: 600; color: #4F46E5;
          background: rgba(255,255,255,0.65);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(99,102,241,0.18);
          transition: all 0.25s ease;
          white-space: nowrap;
          padding: 0 14px;
        }
        .hero-chip:hover {
          transform: translateY(-2px);
          background: #fff;
          box-shadow: 0 6px 20px rgba(124,58,237,0.14);
          border-color: #7C3AED;
        }

        /* ── Hero CTA row ── */
        .hero-cta-wrap {
          display: flex;
          flex-wrap: nowrap;
          gap: 14px;
          align-items: center;
          margin-bottom: 36px;
        }
        @media (max-width: 1024px) {
          .hero-cta-wrap { flex-wrap: wrap; }
        }
        @media (max-width: 640px) {
          .hero-cta-wrap { flex-direction: column; }
          .hero-cta-wrap button { width: 100%; }
        }
        .hero-cta-primary {
          display: flex; align-items: center; justify-content: center; gap: 10px;
          height: 58px; padding: 0 34px; border-radius: 16px; border: none;
          font-size: 16px; font-weight: 800; color: #fff; cursor: pointer;
          min-width: 240px;
          background: linear-gradient(135deg, #6366F1, #7C3AED);
          box-shadow: 0 8px 28px rgba(99,102,241,0.38);
          transition: transform 0.25s ease, box-shadow 0.25s ease;
          position: relative; overflow: hidden; white-space: nowrap;
          flex-shrink: 0;
        }
        .hero-cta-primary::after {
          content:''; position:absolute; inset:0;
          background:linear-gradient(105deg,transparent 35%,rgba(255,255,255,.22) 50%,transparent 65%);
          background-size:200% 100%; animation:shimmer 2.4s linear infinite;
        }
        .hero-cta-primary:hover { transform:translateY(-3px); box-shadow:0 16px 40px rgba(99,102,241,0.48); }
        .hero-cta-secondary {
          display: flex; align-items: center; justify-content: center; gap: 9px;
          height: 58px; padding: 0 26px; border-radius: 16px; cursor: pointer;
          font-size: 15px; font-weight: 700; color: #4F46E5;
          background: #fff;
          border: 1.5px solid rgba(99,102,241,0.22);
          transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
          white-space: nowrap; flex-shrink: 0;
        }
        .hero-cta-secondary:hover {
          transform: translateY(-3px);
          border-color: #7C3AED;
          box-shadow: 0 10px 28px rgba(99,102,241,0.12);
        }

        /* ── Hero info bar (bottom strip) ── */
        .hero-info-bar {
          display: inline-flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          min-height: 88px;
          padding: 16px 32px;
          border-radius: 20px;
          background: rgba(255,255,255,0.85);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(109,74,255,0.12);
          box-shadow: 0 12px 40px rgba(109,74,255,0.08);
          box-sizing: border-box;
          flex-wrap: wrap;
          max-width: 100%;
        }
        @media (max-width: 1024px) {
          .hero-info-bar {
            justify-content: center;
            gap: 24px;
          }
        }
        .hero-info-item {
          display: flex; align-items: center; gap: 10px;
          font-size: 15px; font-weight: 600; color: #374151;
          transition: transform 0.2s ease;
          cursor: default;
          white-space: nowrap;
        }
        .hero-info-item:hover { transform: translateY(-2px); }
        .hero-info-dot {
          width: 1px; height: 28px;
          background: rgba(109,74,255,0.15);
          flex-shrink: 0;
        }
        /* Hero product card */
        .hero-product-card {
          border-radius: 28px; overflow: hidden;
          border: 1px solid rgba(99,102,241,0.15);
          box-shadow: 0 32px 80px rgba(99,102,241,0.13);
          background: #fff;
          width: 100%;
          transition: transform 0.35s ease, box-shadow 0.35s ease;
        }
        .hero-product-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 48px 100px rgba(99,102,241,0.20);
        }
        .floating-dashboard {
          width: 100%;
          animation: float-dashboard 6s ease-in-out infinite;
        }
        /* Responsive hero layout */
        @media (max-width: 900px) {
          .intro-two-col { text-align: center; }
          .hero-cta-wrap { justify-content: center; }
          .hero-brand-block { align-items: center !important; }
        }
      `}</style>

      {/* ══════════════════════════════════════════════════════════
          SECTION 0 — HERO
      ══════════════════════════════════════════════════════════ */}
      <section style={{
        position:'relative', overflow:'hidden',
        background:'linear-gradient(135deg,#FCFBFF 0%,#F8F5FF 35%,#F0EBFF 70%,#FAFAFE 100%)',
        minHeight:'92vh',
        display:'flex', alignItems:'start',
        paddingTop:48, paddingBottom:64,
      }}>
        {/* Subtle dot-grid texture */}
        <div style={{ position:'absolute', inset:0, opacity:.02,
          backgroundImage:'radial-gradient(circle,#7C3AED 1px,transparent 1px)',
          backgroundSize:'32px 32px', pointerEvents:'none' }} />
        {/* Ambient glows */}
        <div style={{ position:'absolute', top:'10%', left:'-6%', width:700, height:700, borderRadius:'50%',
          background:'radial-gradient(circle,rgba(139,92,246,0.10) 0%,transparent 70%)', pointerEvents:'none' }} />
        <div style={{ position:'absolute', top:'20%', right:'-2%', width:520, height:520, borderRadius:'50%',
          background:'radial-gradient(circle,rgba(99,102,241,0.08) 0%,transparent 70%)', pointerEvents:'none' }} />
        <div style={{ position:'absolute', bottom:'0%', left:'30%', width:400, height:400, borderRadius:'50%',
          background:'radial-gradient(circle,rgba(167,139,250,0.06) 0%,transparent 70%)', pointerEvents:'none' }} />

        <div style={{ width:'100%', margin:'0 auto',
          padding:'0 clamp(48px,6vw,96px)', boxSizing:'border-box', position:'relative', zIndex:1 }}>

          <div className="intro-two-col">

            {/* ── LEFT COLUMN ──────────────────────────────────── */}
            <FadeUp>
              {/* ── Brand identity block ── */}
              <div className="hero-brand-block" style={{
                display:'flex',
                flexDirection:'row',
                alignItems:'center',
                gap:22,
                marginBottom:32
              }}>
                {/* Logo */}
                <div style={{ position:'relative', flexShrink:0 }}>
                  <img src="/logo192.png" alt="The Skill Genie"
                    style={{
                      width:96,
                      height:96,
                      objectFit:'contain',
                      display:'block',
                      borderRadius:22,
                      filter:'drop-shadow(0 10px 28px rgba(139,92,246,0.32))'
                    }}
                    onError={e => { e.currentTarget.src='/logo.jpg'; }} />
                </div>

                <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                  {/* Brand name */}
                  <div style={{
                    fontSize:36,
                    fontWeight:800,
                    letterSpacing:'-.02em',
                    lineHeight:1.1,
                    background:'linear-gradient(135deg,#7C3AED 0%,#4F46E5 100%)',
                    WebkitBackgroundClip:'text',
                    WebkitTextFillColor:'transparent',
                    backgroundClip:'text'
                  }}>
                    THE SKILL GENIE
                  </div>

                  {/* Tagline */}
                  <div style={{ fontSize:14, fontStyle:'italic', fontWeight:500, color:'#7C3AED', lineHeight:1.4 }}>
                    Bridging the Gap from Learning to Industry Readiness.
                  </div>

                  {/* Enterprise trust badge */}
                  <div style={{
                    display:'inline-flex', alignItems:'center', gap:6,
                    marginTop:6, padding:'4px 12px', borderRadius:99,
                    background:'rgba(99,102,241,0.07)',
                    border:'1px solid rgba(99,102,241,0.15)',
                  }}>
                    <span style={{ fontSize:11, fontWeight:700, color:'#4F46E5', letterSpacing:'.04em' }}>
                      AI-Powered&nbsp;•&nbsp;White-Labeled&nbsp;•&nbsp;Placement-Focused&nbsp;•&nbsp;Enterprise Ready
                    </span>
                  </div>
                </div>
              </div>

              {/* ── Headline ── */}
              <h1 className="intro-hero-h1">
                <span style={{ color: '#0F172A' }}>We don't replace your curriculum. </span>
                <span className="premium-gradient-text">We complete the journey from classroom learning to industry readiness.</span>
              </h1>

              {/* ── Description ── */}
              <p className="intro-hero-p" style={{ marginBottom:40 }}>
                A fully white-labeled, AI-powered ecosystem that brings together domain learning, coding assessments, AI mock interviews, placement readiness, and institutional analytics—empowering colleges to prepare industry-ready graduates from a single platform.
              </p>

              {/* ── Feature chips — 4 × 2 CSS grid ── */}
              <FadeUp delay={260}>
                <div className="hero-chips-container">
                  {[
                    'AI Powered','White-Label Platform','Placement Ready','Domain Learning',
                    'Guided Bootcamps','Mock Interviews','Digital Certificates','Institutional Analytics',
                  ].map((chip, i) => (
                    <div key={i} className="hero-chip">
                      <CheckCircle2 size={13} style={{ flexShrink:0, color:'#7C3AED' }} /> {chip}
                    </div>
                  ))}
                </div>
              </FadeUp>

              {/* ── Pricing Highlight ── */}
              <FadeUp delay={340}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 16,
                  background: 'linear-gradient(135deg, rgba(124,58,237,0.08) 0%, rgba(99,102,241,0.06) 100%)',
                  border: '1.5px solid rgba(124,58,237,0.2)',
                  borderRadius: 16,
                  padding: '14px 22px',
                  marginBottom: 24,
                  marginTop: 8,
                  backdropFilter: 'blur(8px)',
                  boxShadow: '0 4px 24px rgba(124,58,237,0.08)',
                  flexWrap: 'wrap',
                  gap: 12,
                }}>
                  {/* Price badge */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 3,
                      background: 'linear-gradient(135deg, #7C3AED, #6366F1)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}>
                      <span style={{ fontSize: 30, fontWeight: 900, lineHeight: 1 }}>₹41</span>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>/day</span>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#6D28D9', letterSpacing: '0.03em' }}>
                      per student · per day
                    </span>
                  </div>

                  {/* Divider */}
                  <div style={{ width: 1, height: 44, background: 'rgba(124,58,237,0.2)', flexShrink: 0 }} />

                  {/* Label */}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1E1B4B', lineHeight: 1.4 }}>
                      All features included · Billed annually
                    </div>
                    <div style={{ fontSize: 11, color: '#6B7280', marginTop: 3, fontWeight: 500 }}>
                      AI learning · coding · mock interviews · analytics
                    </div>
                  </div>

                  {/* Tag */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    flexShrink: 0,
                  }}>
                    <div style={{
                      background: 'linear-gradient(135deg, #7C3AED, #6366F1)',
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '4px 10px',
                      borderRadius: 99,
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                    }}>
                      Yearly Plan
                    </div>
                    <div style={{ fontSize: 10, color: '#7C3AED', fontWeight: 600 }}>Best Value</div>
                  </div>
                </div>
              </FadeUp>

              {/* ── CTA buttons — single flex row ── */}
              <FadeUp delay={380}>
                <div className="hero-cta-wrap">
                  <button className="hero-cta-primary" onClick={() => navigate('/collegesignup')} style={{ width: '100%', maxWidth: '300px' }}>
                    <Rocket size={18} style={{ flexShrink:0 }} /> Request Institutional Demo
                  </button>
                  <button className="hero-cta-secondary" onClick={scrollToWhy}>
                    Explore Platform Features <ArrowRight size={16} style={{ flexShrink:0 }} />
                  </button>
                </div>
              </FadeUp>

            </FadeUp>

            {/* ── RIGHT COLUMN — product preview card ──────────── */}
            <FadeUp delay={160}>
              {/* Soft purple glow behind card */}
              <div style={{ position:'relative' }}>
                <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)',
                  width:'80%', height:'80%', borderRadius:'50%',
                  background:'radial-gradient(circle,rgba(124,58,237,0.18) 0%,transparent 70%)',
                  pointerEvents:'none', zIndex:0 }} />
              <div className="floating-dashboard" style={{ position:'relative', zIndex:1 }}>
                <div className="hero-product-card"
                  onMouseEnter={e => { e.currentTarget.style.transform='translateY(-10px)'; e.currentTarget.style.boxShadow='0 40px 90px rgba(99,102,241,0.22)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)';    e.currentTarget.style.boxShadow='0 24px 64px rgba(99,102,241,0.14)'; }}
                >
                  {/* Header */}
                  <div style={{ background:'var(--primary-gradient)', padding:'32px 32px 26px', color:'#fff' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:16 }}>
                      <div style={{ width:50, height:50, borderRadius:14, background:'rgba(255,255,255,0.15)',
                        backdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <Globe size={24} />
                      </div>
                      <div>
                        <div style={{ fontSize:17, fontWeight:800, color:'#fff', lineHeight:1.2 }}>The Skill Genie</div>
                        <div style={{ fontSize:12, opacity:0.85, marginTop:3 }}>Institutional AI Learning Platform</div>
                      </div>
                    </div>
                    {/* Badge */}
                    <div style={{ display:'inline-flex', alignItems:'center', gap:6, background:'rgba(255,255,255,0.12)', 
                      padding:'6px 12px', borderRadius:99, marginBottom:22, border:'1px solid rgba(255,255,255,0.1)' }}>
                      <div style={{ width:6, height:6, borderRadius:'50%', background:'#fff', boxShadow:'0 0 8px #fff' }} />
                      <span style={{ fontSize:10, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                        AI-Powered • White-Labeled • Enterprise Ready
                      </span>
                    </div>

                    {/* Feature Tiles Grid */}
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:10 }}>
                      {[
                        { icon: <Brain size={16} />, title: 'AI Learning Engine', desc: 'Personalized learning paths' },
                        { icon: <Target size={16} />, title: 'AI Mock Interviews', desc: 'Role-based interview practice' },
                        { icon: <Code2 size={16} />, title: 'Coding Assessments', desc: 'Real-time coding evaluation' },
                        { icon: <BarChart3 size={16} />, title: 'Institutional Analytics', desc: 'Department performance insights' },
                      ].map((f, i) => (
                        <div key={i} style={{ 
                          background:'rgba(255,255,255,0.1)', 
                          backdropFilter:'blur(10px)',
                          padding:'12px', 
                          borderRadius:14, 
                          border:'1px solid rgba(255,255,255,0.12)',
                          transition:'all 0.3s ease'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,0.18)'; e.currentTarget.style.transform='translateY(-2px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.1)'; e.currentTarget.style.transform='translateY(0)'; }}
                        >
                          <div style={{ color:'#fff', marginBottom:8, opacity:0.9 }}>{f.icon}</div>
                          <div style={{ fontSize:11, fontWeight:800, color:'#fff', marginBottom:2 }}>{f.title}</div>
                          <div style={{ fontSize:9, color:'rgba(255,255,255,0.7)', lineHeight:1.3 }}>{f.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Body */}
                  <div style={{ padding:'26px 32px' }}>
                    <div style={{ fontSize:10, fontWeight:800, textTransform:'uppercase',
                      color:'#94A3B8', letterSpacing:'.1em', marginBottom:16 }}>Enterprise Platform Modules</div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:11 }}>
                      {[
                        'AI Learning Paths',      'Guided Bootcamps',
                        'Launchpad Roadmaps',      'AI Mock Interviews',
                        'Coding Assessments',      'Resume Evaluation',
                        'GitHub Portfolio Builder', 'AI Code Review',
                        'Skill Analytics',         'Placement Dashboard',
                        'Digital Certificates',    'Recruiter Connect',
                      ].map((item,i) => (
                        <div key={i} style={{ display:'flex', alignItems:'center', gap:8,
                          fontSize:12, color:'#475569', fontWeight:500 }}>
                          <CheckCircle2 size={13} color="#7C3AED" style={{ flexShrink:0 }} />
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Footer */}
                  <div style={{ padding:'16px 32px',
                    background:'linear-gradient(90deg,rgba(99,102,241,0.05),rgba(139,92,246,0.08))',
                    borderTop:'1px solid rgba(99,102,241,0.08)', textAlign:'center' }}>
                    <div style={{ 
                      display:'inline-flex', 
                      alignItems:'center', 
                      gap:8, 
                      padding:'6px 16px', 
                      borderRadius:99, 
                      background:'rgba(79, 70, 229, 0.08)',
                      border:'1px solid rgba(79, 70, 229, 0.15)'
                    }}>
                      <Sparkles size={14} color="#4F46E5" />
                      <span style={{ fontSize:12, fontWeight:700, color:'#4F46E5' }}>
                        Unified AI Learning • Assessment • Placement Platform
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              </div>{/* /glow wrapper */}
            </FadeUp>

          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          SECTION 1 — PLATFORM HIGHLIGHTS
      ══════════════════════════════════════════════════════════ */}
      <section
        className="ip-section-wrapper"
        style={{
          background: 'var(--hero-bg)',
          overflow: 'hidden',
        }}
      >
        <div style={{ position:'absolute', top:'-100px', left:'-100px', width:'600px', height:'600px', borderRadius:'50%', background:'radial-gradient(circle,rgba(124,58,237,0.15) 0%,transparent 70%)', pointerEvents:'none' }} />
        <div style={{ position:'absolute', bottom:'-80px', right:'-80px', width:'500px', height:'500px', borderRadius:'50%', background:'radial-gradient(circle,rgba(99,102,241,0.15) 0%,transparent 70%)', pointerEvents:'none' }} />

        <div className="ip-container" style={{ position:'relative', zIndex:1 }}>
          <FadeUp style={{ textAlign:'center', marginBottom:64 }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'6px 18px', borderRadius:'99px', background:'rgba(255,255,255,.1)', border:'1px solid rgba(255,255,255,.2)', fontSize:12, fontWeight:800, color:'#C4B5FD', letterSpacing:'.11em', textTransform:'uppercase', marginBottom:24 }}>
              <Sparkles size={14} /> Platform Capabilities
            </div>
            <h2 style={{ color:'#fff', marginBottom:20 }}>
              Everything Your Institution Needs.<br />
              <span className="premium-gradient-text">One Unified Ecosystem.</span>
            </h2>
            <p style={{ color:'rgba(255,255,255,0.65)', maxWidth:680, margin:'0 auto' }}>
              Built to help institutional leaders deliver placement-ready graduates—without stitching together multiple platforms.
            </p>
          </FadeUp>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))', gap:24, maxWidth:1200, margin:'0 auto' }}>
            {[
              { icon:<BookOpen size={22}/>,   label:'90+ Technology Learning Tracks',          desc:'Industry-mapped domain learning paths across software, data, and AI.' },
              { icon:<Rocket size={22}/>,     label:'Guided Career Bootcamps',                  desc:'Structured cohort-based programs designed to master specific technical domains.' },
              { icon:<Cpu size={22}/>,        label:'AI-Powered Coding Assessments',            desc:'Automated evaluation with AI feedback and performance benchmarks.' },
              { icon:<Bot size={22}/>,        label:'AI Mock Interviews & Evaluation',          desc:'Role-based interview simulation with instant detailed feedback.' },
              { icon:<FileCheck size={22}/>,  label:'Resume & GitHub Portfolio Builder',        desc:'Structured profiles that make students recruiter-ready.' },
              { icon:<BarChart3 size={22}/>,  label:'Institutional Analytics Dashboard',        desc:'Real-time skill, placement, and learning insights for leadership.' },
              { icon:<Globe size={22}/>,      label:'White-Labeled Student Portal',             desc:'Your brand. Your domain. Fully customized digital campus.' },
              { icon:<Target size={22}/>,     label:'Placement Readiness Ecosystem',            desc:'End-to-end pipeline from skill development to career outcomes.' },
            ].map((c, i) => (
              <FadeUp key={i} delay={i * 50}>
                <div
                  className="ip-card-glass"
                  style={{ display:'flex', flexDirection:'column', gap:14, padding:'28px 28px' }}
                  onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,0.10)'; e.currentTarget.style.transform='translateY(-4px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.05)'; e.currentTarget.style.transform='translateY(0)'; }}
                >
                  <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                    <div style={{ width:44, height:44, borderRadius:12, background:'rgba(196,181,253,0.15)', border:'1px solid rgba(196,181,253,0.2)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, color:'#C4B5FD' }}>
                      {c.icon}
                    </div>
                    <div style={{ width:22, height:22, borderRadius:'50%', background:'linear-gradient(135deg,#10B981,#059669)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <span style={{ fontSize:10, color:'#fff', fontWeight:900 }}>✓</span>
                    </div>
                  </div>
                  <div>
                    <p style={{ margin:'0 0 6px', fontSize:15, fontWeight:800, color:'#F1F5F9', lineHeight:1.3 }}>{c.label}</p>
                    <p style={{ margin:0, fontSize:13, color:'rgba(255,255,255,0.55)', lineHeight:1.6, fontWeight:400 }}>{c.desc}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>

          {/* Bottom CTA row removed */}
        </div>
      </section>

      <section className="ip-section-wrapper" style={{ background: '#F8FAFC' }}>
        <div className="ip-container">
          <FadeUp style={{ textAlign: 'center' }}>
            <h2 style={{ marginBottom: 24 }}>White-Label AI Digital Campus for Every Institution</h2>
            <p style={{ marginBottom: 48 }}>
              Every partner institution receives a fully branded digital ecosystem including a dedicated College Admin Portal and Student Portal with custom branding, personalized login experience, institution-specific identity, AI-powered learning infrastructure, analytics dashboards, and placement intelligence — without the cost or complexity of building a platform from scratch.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'center' }}>
              {['Custom Branding', 'Dedicated Subdomain', 'College Admin Portal', 'Student Portal', 'White-Label Ready'].map((badge, i) => (
                <div key={i} className="premium-chip" style={{ fontSize: 16, padding: '12px 28px' }}>
                  <BadgeCheck size={20} /> {badge}
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      <SectionDivider />

      {/* ══════════════════════════════════════════════════════════
          SECTION 3 — WHITE LABEL
      ══════════════════════════════════════════════════════════ */}
      <section className="ip-section-wrapper" style={{ background: 'var(--hero-bg)', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, opacity:.03, backgroundImage:'linear-gradient(rgba(255,255,255,.7) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.7) 1px,transparent 1px)', backgroundSize:'40px 40px', pointerEvents:'none' }} />
        
        <div className="ip-container" style={{ position:'relative', zIndex:1 }}>
          <FadeUp style={{ textAlign:'center', marginBottom:72 }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'6px 18px', borderRadius:'99px', background:'rgba(255,255,255,.1)', border:'1px solid rgba(255,255,255,.2)', fontSize:12, fontWeight:800, color:'#C4B5FD', letterSpacing:'.11em', textTransform:'uppercase', marginBottom:24 }}>
              <Palette size={14} /> Branded Campus Experience
            </div>
            <h2 style={{ color:'#fff', marginBottom: 24 }}>
              Complete Digital Ecosystem for<br />
              <span className="premium-gradient-text">Institution Management &amp; Student Success</span>
            </h2>
            <p style={{ color:'rgba(255,255,255,0.7)', maxWidth:900 }}>
              The Skill Genie provides an integrated AI-powered ecosystem that enables institutions to manage operations efficiently while empowering students with industry-ready learning, coding practice, placement preparation, portfolio building, and career growth.
            </p>
          </FadeUp>

          <div className="wl-cards">
            {/* Admin Portal Card */}
            <FadeUp delay={0} className="wl-card-item">
              <div className="ip-card-glass" style={{ height: '100%', padding: '0 !important', overflow: 'hidden', display:'flex', flexDirection:'column' }}>
                {/* Card header */}
                <div style={{ background:'linear-gradient(135deg,rgba(79,70,229,.4),rgba(124,58,237,.3))', padding:'32px', display:'flex', alignItems:'center', gap:16, borderBottom:'1px solid rgba(255,255,255,.1)' }}>
                  <div style={{ width:56, height:56, borderRadius:16, background:'rgba(255,255,255,.15)', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 8px 16px rgba(0,0,0,0.1)' }}>
                    <Building2 size={28} color="#fff" />
                  </div>
                  <div>
                    <h4 style={{ margin:0, fontSize:22, fontWeight:800, color:'#fff', letterSpacing:'-0.01em' }}>College Admin Portal</h4>
                    <p style={{ margin:'4px 0 0 0', fontSize:14, color:'rgba(196,181,253,.9)', fontWeight:500 }}>Institutional Management Dashboard</p>
                  </div>
                </div>
                <div style={{ padding:'32px', flex:1, display:'flex', flexDirection:'column', justifyContent:'center' }}>
                  <div className="portal-feature-grid">
                    {[
                      { icon:<Layout size={18}/>,     label:'Institution Branding' },
                      { icon:<Globe size={18}/>,      label:'Custom Subdomain' },
                      { icon:<Users size={18}/>,      label:'Student Analytics' },
                      { icon:<TrendingUp size={18}/>,  label:'Performance Tracking' },
                      { icon:<BarChart3 size={18}/>,  label:'Reports & Insights' },
                      { icon:<Award size={18}/>,      label:'Certificate Management' },
                      { icon:<Brain size={18}/>,      label:'AI Intelligence' },
                      { icon:<Shield size={18}/>,     label:'Role Management' },
                      { icon:<Activity size={18}/>,   label:'Engagement Tracking' },
                      { icon:<GitBranch size={18}/>,  label:'Git Portfolio Management' },
                      { icon:<Bot size={18}/>,        label:'AI Code Review Dashboard' },
                    ].map((f, i) => (
                      <div key={i} className="portal-feature-card">
                        <div className="portal-icon-box" style={{ color:'#A78BFA' }}>{f.icon}</div>
                        <span className="portal-feature-label">{f.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </FadeUp>

            {/* Student Portal Card */}
            <FadeUp delay={120} className="wl-card-item">
              <div className="ip-card-glass" style={{ height: '100%', padding: '0 !important', overflow: 'hidden', display:'flex', flexDirection:'column' }}>
                {/* Card header */}
                <div style={{ background:'linear-gradient(135deg,rgba(16,185,129,.3),rgba(6,182,212,.2))', padding:'32px', display:'flex', alignItems:'center', gap:16, borderBottom:'1px solid rgba(255,255,255,.1)' }}>
                  <div style={{ width:56, height:56, borderRadius:16, background:'rgba(255,255,255,.15)', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 8px 16px rgba(0,0,0,0.1)' }}>
                    <GraduationCap size={28} color="#fff" />
                  </div>
                  <div>
                    <h4 style={{ margin:0, fontSize:22, fontWeight:800, color:'#fff', letterSpacing:'-0.01em' }}>Student Portal</h4>
                    <p style={{ margin:'4px 0 0 0', fontSize:14, color:'rgba(167,243,208,.9)', fontWeight:500 }}>Personalised Learning Experience</p>
                  </div>
                </div>
                <div style={{ padding:'32px', flex:1, display:'flex', flexDirection:'column', justifyContent:'center' }}>
                  <div className="portal-feature-grid">
                    {[
                      { icon:<Palette size={18}/>,        label:'College Branding' },
                      { icon:<Lock size={18}/>,            label:'Dedicated Login' },
                      { icon:<Monitor size={18}/>,         label:'Learning Dashboard' },
                      { icon:<BookOpen size={18}/>,        label:'Guided Bootcamps' },
                      { icon:<Bot size={18}/>,             label:'Mock Interviews' },
                      { icon:<Code2 size={18}/>,           label:'Coding Practice' },
                      { icon:<GitBranch size={18}/>,       label:'Git Portfolio Builder' },
                      { icon:<Cpu size={18}/>,             label:'AI Code Review' },
                      { icon:<Award size={18}/>,           label:'Digital Certificates' },
                      { icon:<TrendingUp size={18}/>,      label:'Placement Readiness' },
                      { icon:<Briefcase size={18}/>,       label:'Career Tracking' },
                      { icon:<MessageSquare size={18}/>,   label:'AI Learning Assistant' },
                    ].map((f, i) => (
                      <div key={i} className="portal-feature-card">
                        <div className="portal-icon-box" style={{ color:'#6EE7B7' }}>{f.icon}</div>
                        <span className="portal-feature-label">{f.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </FadeUp>
          </div>

          <FadeUp delay={240}>
            <div style={{ marginTop:64, padding:'32px 48px', borderRadius:24, background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.1)', backdropFilter:'blur(8px)', textAlign:'center', width: '100%' }}>
              <p style={{ margin:0, fontSize:16, color:'rgba(199,210,254,0.7)', fontWeight: 500 }}>
                🚀 Every institution receives its own branded AI-powered digital campus — a seamless, enterprise-grade identity for administrators, faculty, and students with zero development overhead.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      <SectionDivider />

      {/* ══════════════════════════════════════════════════════════
          SECTION 4 — WHY THE SKILL GENIE (Connected Timeline)
      ══════════════════════════════════════════════════════════ */}
      <section ref={whyRef} className="ip-section-wrapper" style={{ background:'#FAFBFF' }}>
        <div className="ip-container">

          <FadeUp style={{ textAlign:'center', marginBottom:80 }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'6px 18px', borderRadius:'99px', background:'rgba(99,102,241,.06)', border:'1px solid rgba(99,102,241,.12)', fontSize:12, fontWeight:800, color:'#4F46E5', letterSpacing:'.11em', textTransform:'uppercase', marginBottom:24 }}>
              <Star size={14} /> Platform Capabilities
            </div>
            <h2 style={{ marginBottom: 24 }}>
              Everything Students Need. One Intelligent Platform.
            </h2>
            <p style={{ maxWidth:900 }}>
              A seamless, connected journey from foundational learning to career-ready employment through our proprietary AI-powered roadmap.
            </p>
          </FadeUp>

          {/* Timeline */}
          <div style={{ position:'relative', maxWidth:1400, margin:'0 auto' }}>
            <div style={{ position:'absolute', left:'50%', top:20, bottom:20, width:2, background:'var(--primary-gradient)', transform:'translateX(-50%)', opacity:.15, borderRadius:2 }} />

            {[
              { emoji:'📚', color:'#4F46E5', bg:'rgba(79,70,229,.09)',   border:'rgba(79,70,229,.12)',  step:'01', title:'Domain Learning',     desc:'AI-structured learning journeys aligned with real industry roles and modern technology stacks.' },
              { emoji:'💻', color:'#7C3AED', bg:'rgba(124,58,237,.09)',  border:'rgba(124,58,237,.12)', step:'02', title:'Coding Practice',     desc:'Hands-on coding challenges and enterprise-scenario exercises that build practical programming proficiency.' },
              { emoji:'⚙',  color:'#0EA5E9', bg:'rgba(14,165,233,.09)',  border:'rgba(14,165,233,.12)', step:'03', title:'Real Projects',       desc:'Implementation-focused bootcamp projects mirroring real-world software development workflows.' },
              { emoji:'🧠', color:'#10B981', bg:'rgba(16,185,129,.09)',  border:'rgba(16,185,129,.12)', step:'04', title:'AI Code Review',      desc:'Intelligent code analysis with detailed optimization recommendations and industry best-practices.' },
              { emoji:'🤖', color:'#F59E0B', bg:'rgba(245,158,11,.09)',  border:'rgba(245,158,11,.12)', step:'05', title:'Mock Interviews',     desc:'AI-generated technical interviews with domain-specific questions and detailed evaluation scoring.' },
              { emoji:'📜', color:'#EC4899', bg:'rgba(236,72,153,.09)',  border:'rgba(236,72,153,.12)', step:'06', title:'Certifications',      desc:'Industry-ready digital credentials backed by verified practical skill achievements.' },
              { emoji:'🚀', color:'#8B5CF6', bg:'rgba(139,92,246,.09)',  border:'rgba(139,92,246,.12)', step:'07', title:'Placement Readiness', desc:'Recruiter-ready profiles built from real project contributions and assessment performance.' },
              { emoji:'🏆', color:'#D97706', bg:'rgba(217,119,6,.09)',   border:'rgba(217,119,6,.12)',  step:'08', title:'Career Success',      desc:'Graduates who are technically skilled, practically experienced, and immediately employable.' },
            ].map((item, i) => {
              const isLeft = i % 2 === 0;
              return (
                <div key={i} className="timeline-row">
                  <div className="tl-card-left">
                    {isLeft ? (
                      <FadeUp delay={i * 70} style={{ width:'100%', maxWidth:430 }}>
                        <div className="ip-card">
                          <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:16 }}>
                            <div style={{ width:52, height:52, borderRadius:14, background:item.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, flexShrink:0 }}>
                              {item.emoji}
                            </div>
                            <div>
                              <span style={{ fontSize:11, fontWeight:800, color:item.color, textTransform:'uppercase', letterSpacing:'.10em' }}>Step {item.step}</span>
                              <h4 style={{ margin:'2px 0 0', fontSize:18, fontWeight:800, color:'#0F172A', letterSpacing:'-.01em' }}>{item.title}</h4>
                            </div>
                          </div>
                          <p style={{ margin:0, fontSize:15, color:'#64748B', lineHeight:1.7, textAlign:'left', marginLeft:0 }}>{item.desc}</p>
                        </div>
                      </FadeUp>
                    ) : <div className="tl-empty" />}
                  </div>

                  <div className="tl-dot-wrap" style={{ display:'flex', justifyContent:'center', alignItems:'center', zIndex:2 }}>
                    <FadeUp delay={i * 70 + 30}>
                      <div style={{ width:48, height:48, borderRadius:'50%', background:`linear-gradient(135deg,${item.color},${item.color}CC)`, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:`0 0 0 6px white, 0 10px 30px rgba(0,0,0,0.1)`, fontSize:20, animation:'pulse 3s ease-in-out infinite', animationDelay:`${i * 0.3}s` }}>
                        {item.emoji}
                      </div>
                    </FadeUp>
                  </div>

                  <div className="tl-card-right">
                    {!isLeft ? (
                      <FadeUp delay={i * 70} style={{ width:'100%', maxWidth:430 }}>
                        <div className="ip-card">
                          <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:16 }}>
                            <div style={{ width:52, height:52, borderRadius:14, background:item.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, flexShrink:0 }}>
                              {item.emoji}
                            </div>
                            <div>
                              <span style={{ fontSize:11, fontWeight:800, color:item.color, textTransform:'uppercase', letterSpacing:'.10em' }}>Step {item.step}</span>
                              <h4 style={{ margin:'2px 0 0', fontSize:18, fontWeight:800, color:'#0F172A', letterSpacing:'-.01em' }}>{item.title}</h4>
                            </div>
                          </div>
                          <p style={{ margin:0, fontSize:15, color:'#64748B', lineHeight:1.7, textAlign:'left', marginLeft:0 }}>{item.desc}</p>
                        </div>
                      </FadeUp>
                    ) : <div className="tl-empty" />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* ══════════════════════════════════════════════════════════
          SECTION 5 — COMPARISON
      ══════════════════════════════════════════════════════════ */}
      <section className="ip-section-wrapper" style={{ background:'linear-gradient(180deg,#F5F7FF 0%,#FAFBFF 100%)' }}>
        <div className="ip-container">

          <FadeUp style={{ textAlign:'center', marginBottom:80 }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'6px 18px', borderRadius:'99px', background:'rgba(99,102,241,.06)', border:'1px solid rgba(99,102,241,.12)', fontSize:12, fontWeight:800, color:'#4F46E5', letterSpacing:'.11em', textTransform:'uppercase', marginBottom:24 }}>
              <Zap size={14} /> Platform Comparison
            </div>
            <h2 style={{ marginBottom: 24 }}>
              Why Leading Institutions Are Moving Beyond<br />
              <span className="premium-gradient-text">Traditional Learning Models</span>
            </h2>
            <p style={{ maxWidth:900 }}>
              Institutional leaders need a platform that drives measurable placement outcomes—not just content delivery. Here's how The Skill Genie changes the equation.
            </p>
          </FadeUp>

          <div className="comparison-grid">

            {/* Conventional */}
            <FadeUp delay={0} className="comparison-card">
              <div className="ip-card">
                <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:32 }}>
                  <div style={{ width:52, height:52, borderRadius:14, background:'#F1F5F9', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <Monitor size={24} color="#94A3B8" />
                  </div>
                  <div>
                    <p style={{ margin:'0 0 2px', fontSize:11, fontWeight:800, color:'#94A3B8', textTransform:'uppercase', letterSpacing:'.10em' }}>Traditional Approach</p>
                    <h3 style={{ margin:0, fontSize:18, fontWeight:800, color:'#475569' }}>Traditional Learning Model</h3>
                  </div>
                </div>
                <div style={{ display: 'grid', gap: 16 }}>
                  {[
                    'Generic curriculum with limited industry relevance',
                    'Theory-focused learning with minimal practical exposure',
                    'Hours of manual Excel tracking and reporting',
                    'Multiple disconnected platforms for learning and assessments',
                    'Limited placement readiness preparation',
                    'Manual student progress monitoring',
                    'Static course delivery without personalization',
                    'Blind recruitment without technical pre-screening',
                  ].map((item, i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:14, paddingBottom: 16, borderBottom: i < 7 ? '1px solid #F1F5F9' : 'none' }}>
                      <div style={{ width:24, height:24, borderRadius:'50%', background:'#F1F5F9', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <span style={{ fontSize:10, color:'#CBD5E1', fontWeight:900 }}>✗</span>
                      </div>
                      <span style={{ fontSize:15, color:'#94A3B8', fontWeight:500 }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeUp>

            {/* The Skill Genie */}
            <FadeUp delay={140} className="comparison-card">
              <div className="ip-card" style={{
                border: '1px solid rgba(99,102,241,.2)',
                background:'linear-gradient(145deg,rgba(255,255,255,1),rgba(245,247,255,1))',
                boxShadow:'0 20px 60px rgba(99,102,241,.1)',
                position:'relative', overflow:'hidden'
              }}>
                <div style={{ position:'absolute', top:'-40px', right:'-40px', width:200, height:200, borderRadius:'50%', background:'radial-gradient(circle,rgba(124,58,237,.08) 0%,transparent 70%)', pointerEvents:'none' }} />
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:32, position:'relative', zIndex:1, flexWrap:'wrap', gap:12 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:16 }}>
                    <div style={{ width:52, height:52, borderRadius:14, background:'var(--primary-gradient)', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 8px 20px rgba(99,102,241,.3)' }}>
                      <Sparkles size={24} color="#fff" />
                    </div>
                    <div>
                      <p style={{ margin:'0 0 2px', fontSize:11, fontWeight:800, color:'#6366F1', textTransform:'uppercase', letterSpacing:'.10em' }}>Recommended</p>
                      <h3 style={{ margin:0, fontSize:18, fontWeight:800, color:'#0F172A' }}>The Skill Genie Ecosystem</h3>
                    </div>
                  </div>
                  <span style={{ padding:'6px 14px', borderRadius:'99px', background:'var(--highlight-gradient)', fontSize:11, fontWeight:800, color:'#fff', letterSpacing:'.06em' }}>⭐ PREMIUM</span>
                </div>
                <div style={{ display: 'grid', gap: 16, position:'relative', zIndex:1 }}>
                  {[
                    'Domain-specific industry learning paths',
                    'AI-powered personalized skill development',
                    'Automated coding assessments and evaluations',
                    'AI mock interviews with detailed feedback',
                    'Unified learning, assessment, and placement platform',
                    'Real-time institutional dashboards and skill analytics',
                    'Adaptive AI learning recommendations',
                    'Recruiter-ready AI resume and code screening ecosystem',
                  ].map((item, i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:14, paddingBottom: 16, borderBottom: i < 7 ? '1px solid rgba(99,102,241,.06)' : 'none' }}>
                      <div style={{ width:24, height:24, borderRadius:'50%', background:'linear-gradient(135deg,#10B981,#059669)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, boxShadow:'0 4px 10px rgba(16,185,129,.2)' }}>
                        <span style={{ fontSize:10, color:'#fff', fontWeight:900 }}>✓</span>
                      </div>
                      <span style={{ fontSize:15, color:'#1E293B', fontWeight:600 }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* ══════════════════════════════════════════════════════════
          SECTION 6 — INSTITUTIONAL BENEFITS
      ══════════════════════════════════════════════════════════ */}

      {/* ══════════════════════════════════════════════════════════
          SECTION 7 — STUDENT SUCCESS JOURNEY (REDESIGNED)
      ══════════════════════════════════════════════════════════ */}
      <section
        className="ip-section-wrapper"
        style={{
          background: 'linear-gradient(180deg, #FCFCFF 0%, #F8F5FF 50%, #FFFFFF 100%)',
          paddingTop: 140,
          paddingBottom: 140,
          overflow: 'hidden',
        }}
      >
        {/* Ambient glow blobs */}
        <div style={{ position:'absolute', top:'8%',  left:'-6%',  width:500, height:500, borderRadius:'50%', background:'radial-gradient(circle,rgba(139,92,246,0.07) 0%,transparent 70%)', pointerEvents:'none' }} />
        <div style={{ position:'absolute', bottom:'10%', right:'-4%', width:420, height:420, borderRadius:'50%', background:'radial-gradient(circle,rgba(99,102,241,0.06) 0%,transparent 70%)', pointerEvents:'none' }} />

        <div style={{ width:'100%', maxWidth:1600, margin:'0 auto', padding:'0 clamp(32px,5vw,80px)', boxSizing:'border-box', position:'relative', zIndex:1 }}>

          {/* ── Section header ── */}
          <FadeUp style={{ textAlign:'center', marginBottom:80 }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'6px 20px', borderRadius:'99px', background:'rgba(99,102,241,0.07)', border:'1px solid rgba(99,102,241,0.15)', fontSize:11, fontWeight:800, color:'#4F46E5', letterSpacing:'.12em', textTransform:'uppercase', marginBottom:28 }}>
              <GraduationCap size={13} /> 🎓 Student Success Journey
            </div>
            <h2 className="journey-section-h2">
              One Intelligent Platform.<br />
              <span className="premium-gradient-text">One Complete Student Journey.</span>
            </h2>
            <p style={{ margin:'0 auto', maxWidth:860, fontSize:17, lineHeight:1.8, color:'#64748B', fontWeight:400, textAlign:'center' }}>
              From the moment a student joins the institution until becoming industry-ready, The Skill Genie provides a seamless AI-powered ecosystem that guides every stage of learning, practice, assessment, certification, and placement readiness.
            </p>
          </FadeUp>

          {/* ── Animated timeline rail ── */}
          <FadeUp delay={100}>
            <div style={{ position:'relative', marginBottom:32, height:28, display:'flex', alignItems:'center' }}>
              {/* Gradient line */}
              <div style={{
                position:'absolute', left:0, right:0, height:3,
                background:'linear-gradient(90deg,#7C3AED,#6366F1,#0EA5E9,#10B981,#F59E0B,#EC4899,#8B5CF6,#D97706)',
                borderRadius:99,
                animation:'journeyLineGlow 3s ease-in-out infinite',
                opacity:0.35,
              }} />
              {/* Glowing dots */}
              {[
                '#7C3AED','#3B82F6','#6366F1','#0EA5E9',
                '#10B981','#F59E0B','#EC4899','#8B5CF6','#D97706',
              ].map((c, i) => (
                <div key={i} style={{
                  position:'absolute',
                  left:`${(i / 8) * 100}%`,
                  transform:'translateX(-50%)',
                  width:14, height:14, borderRadius:'50%',
                  background:c,
                  boxShadow:`0 0 0 3px white, 0 0 12px ${c}88`,
                  animation:'journeyBadgePulse 2.5s ease-in-out infinite',
                  animationDelay:`${i * 0.2}s`,
                  zIndex:2,
                }} />
              ))}
            </div>
          </FadeUp>

          {/* ── Journey cards grid ── */}
          <div className="journey-grid">
            {[
              {
                emoji:'🎓', color:'#7C3AED', bg:'rgba(124,58,237,0.09)', border:'rgba(124,58,237,0.18)',
                step:1, caption:'Enrollment',
                title:'Join Institution',
                desc:'Students receive access to a personalized AI-powered learning ecosystem.',
              },
              {
                emoji:'📚', color:'#3B82F6', bg:'rgba(59,130,246,0.09)', border:'rgba(59,130,246,0.18)',
                step:2, caption:'Domain-Centric Learning',
                title:'Choose Learning Path',
                desc:'Structured learning aligned with career goals and industry demands.',
              },
              {
                emoji:'💻', color:'#6366F1', bg:'rgba(99,102,241,0.09)', border:'rgba(99,102,241,0.18)',
                step:3, caption:'Coding Practice',
                title:'Hands-on Coding',
                desc:'Practice through real-world projects, challenges, and enterprise scenarios.',
              },
              {
                emoji:'⚙️', color:'#0EA5E9', bg:'rgba(14,165,233,0.09)', border:'rgba(14,165,233,0.18)',
                step:4, caption:'Implementation Bootcamp',
                title:'Guided Bootcamps',
                desc:'Milestone-driven bootcamps focused on practical implementation.',
              },
              {
                emoji:'🧠', color:'#10B981', bg:'rgba(16,185,129,0.09)', border:'rgba(16,185,129,0.18)',
                step:5, caption:'Instant Feedback',
                title:'AI Code Review',
                desc:'Intelligent code reviews, optimization tips, and best-practice guidance.',
              },
              {
                emoji:'🤖', color:'#F59E0B', bg:'rgba(245,158,11,0.09)', border:'rgba(245,158,11,0.18)',
                step:6, caption:'AI Assessment',
                title:'Mock Interviews',
                desc:'AI-powered interviews with detailed performance analysis and suggestions.',
              },
              {
                emoji:'🏆', color:'#EC4899', bg:'rgba(236,72,153,0.09)', border:'rgba(236,72,153,0.18)',
                step:7, caption:'Digital Certifications',
                title:'Skill Validation',
                desc:'Earn verifiable digital certifications based on practical skills.',
              },
              {
                emoji:'🚀', color:'#8B5CF6', bg:'rgba(139,92,246,0.09)', border:'rgba(139,92,246,0.18)',
                step:8, caption:'Placement Preparation',
                title:'Career Readiness',
                desc:'Build recruiter-ready profiles with Git portfolios and interview readiness.',
              },
              {
                emoji:'🥇', color:'#D97706', bg:'rgba(217,119,6,0.09)', border:'rgba(217,119,6,0.18)',
                step:9, caption:'Career Achievement',
                title:'Industry Success',
                desc:'Graduate industry-ready with practical skills and confidence to excel.',
              },
            ].map((s, i) => (
              <FadeUp key={i} delay={i * 55} style={{ display:'flex' }}>
                <div
                  className="journey-card"
                  style={{ borderColor: s.border }}
                >
                  {/* Subtle top accent line */}
                  <div style={{ position:'absolute', top:0, left:'20%', right:'20%', height:3, borderRadius:'0 0 4px 4px', background:`linear-gradient(90deg,transparent,${s.color},transparent)`, opacity:0.6 }} />

                  {/* Step badge */}
                  <div style={{
                    width:26, height:26, borderRadius:'50%',
                    background:`linear-gradient(135deg,${s.color},${s.color}cc)`,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:11, fontWeight:900, color:'#fff',
                    marginBottom:12, flexShrink:0,
                    boxShadow:`0 2px 8px ${s.color}55`,
                    animation:'journeyBadgePulse 3s ease-in-out infinite',
                    animationDelay:`${i * 0.15}s`,
                  }}>
                    {s.step}
                  </div>

                  {/* Icon */}
                  <div style={{
                    width:58, height:58, borderRadius:'50%',
                    background:s.bg,
                    border:`1.5px solid ${s.border}`,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:26, marginBottom:14, flexShrink:0,
                    animation:'journeyIconFloat 4s ease-in-out infinite',
                    animationDelay:`${i * 0.3}s`,
                  }}>
                    {s.emoji}
                  </div>

                  {/* Caption */}
                  <div style={{
                    fontSize:9, fontWeight:800, textTransform:'uppercase',
                    letterSpacing:'.08em', color:s.color,
                    marginBottom:6, lineHeight:1.3,
                  }}>
                    {s.caption}
                  </div>

                  {/* Title */}
                  <div style={{
                    fontSize:13, fontWeight:800, color:'#0F172A',
                    lineHeight:1.3, marginBottom:8,
                  }}>
                    {s.title}
                  </div>

                  {/* Description */}
                  <div style={{
                    fontSize:11, color:'#64748B', lineHeight:1.65,
                    fontWeight:400, flexGrow:1,
                  }}>
                    {s.desc}
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>

        </div>
      </section>

      <SectionDivider />

      {/* ══════════════════════════════════════════════════════════
          SECTION 8 — FUTURE TECHNOLOGY ROADMAP
      ══════════════════════════════════════════════════════════ */}
      <section className="ip-section-wrapper" style={{ background: 'var(--hero-bg)', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, opacity:.02, backgroundImage:'linear-gradient(rgba(255,255,255,.6) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.6) 1px,transparent 1px)', backgroundSize:'32px 32px', pointerEvents:'none' }} />
        
        <div className="ip-container" style={{ position:'relative', zIndex:1 }}>

          <FadeUp style={{ textAlign:'center', marginBottom:80 }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'6px 18px', borderRadius:'99px', background:'rgba(255,255,255,.1)', border:'1px solid rgba(255,255,255,.2)', fontSize:12, fontWeight:800, color:'#C4B5FD', letterSpacing:'.11em', textTransform:'uppercase', marginBottom:24 }}>
              <Cpu size={14} /> Future Technology Roadmap
            </div>
            <h2 style={{ color:'#fff', marginBottom: 24 }}>
              Preparing Students for <span className="premium-gradient-text">Tomorrow's Technologies</span>
            </h2>
            <p style={{ color:'rgba(255,255,255,0.7)', maxWidth:900 }}>
              Students gain exposure to emerging technologies shaping the future of software engineering and AI — exclusively available for partner institutions.
            </p>
          </FadeUp>

          {/* Glowing roadmap line */}
          <FadeUp delay={60}>
            <div style={{ position:'relative', marginBottom:64, maxWidth:1200, margin:'0 auto 64px' }}>
              <div style={{ height:3, background:'linear-gradient(90deg,transparent,#7C3AED,#C4B5FD,#FCD34D,#C4B5FD,#7C3AED,transparent)', borderRadius:99, animation:'roadmapGlow 3s ease-in-out infinite' }} />
              <div style={{ position:'absolute', top:'50%', transform:'translateY(-50%)', left:'0%', right:'0%', display:'flex', justifyContent:'space-between', paddingLeft:'10%', paddingRight:'10%' }}>
                {[0,1,2,3,4,5].map(i => (
                  <div key={i} style={{ width:12, height:12, borderRadius:'50%', background:'#C4B5FD', boxShadow:'0 0 15px rgba(196,181,253,0.8)', animation:'pulse 2.5s ease-in-out infinite', animationDelay:`${i * 0.25}s` }} />
                ))}
              </div>
            </div>
          </FadeUp>

          <div className="feature-grid">
            {[
              { emoji:'🔗', color:'#C4B5FD', title:'Model Context Protocol', sub:'MCP', desc:'Standardized protocol for connecting AI models with data sources and external tools.' },
              { emoji:'🕸',  color:'#67E8F9', title:'LangGraph',              sub:'AI Workflows', desc:'Build stateful multi-agent AI applications with graph-based orchestration.' },
              { emoji:'⚡', color:'#86EFAC', title:'n8n (Self-Hosted)',       sub:'Automation', desc:'Visual workflow automation for enterprise integrations and AI pipelines.' },
              { emoji:'📱', color:'#FCD34D', title:'FlutterFlow',            sub:'No-Code Dev', desc:'Build native mobile and web apps visually with Flutter and Firebase.' },
            ].map((t, i) => (
              <FadeUp key={i} delay={i * 65}>
                <div className="ip-card-glass" style={{ height:'100%', padding: '32px' }}>
                  <div style={{ fontSize:32, marginBottom:20 }}>{t.emoji}</div>
                  <h4 style={{ margin:'0 0 8px', fontSize:18, fontWeight:800, color:t.color, letterSpacing:'-.01em' }}>{t.title}</h4>
                  <p style={{ margin:'0 0 12px', fontSize:11, fontWeight:700, color:'rgba(196,181,253,.6)', textTransform:'uppercase', letterSpacing:'.09em', textAlign:'left', marginLeft:0 }}>{t.sub}</p>
                  <p style={{ margin:'0 0 24px', fontSize:14, color:'#A5B4FC', lineHeight:1.6, textAlign:'left', marginLeft:0 }}>{t.desc}</p>
                  <span style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:'99px', background:'rgba(251,191,36,.1)', border:'1px solid rgba(251,191,36,.2)', fontSize:11, fontWeight:800, color:'#FCD34D', letterSpacing:'.06em' }}>
                    🏆 Coming Soon
                  </span>
                </div>
              </FadeUp>
            ))}
          </div>

          {/* ─── Exclusive Future Roadmap Section ─── */}
          <FadeUp delay={300} style={{ marginTop: 80, display: 'flex', justifyContent: 'center' }}>
            <div style={{
              maxWidth: '900px',
              width: '100%',
              background: 'linear-gradient(180deg, #FFFFFF 0%, #F8F5FF 100%)',
              border: '1px solid rgba(124,58,237,0.15)',
              borderRadius: '24px',
              padding: '40px',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(99,102,241,0.08)',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden'
            }} className="roadmap-locked-card">
              <style>{`
                .roadmap-locked-card:hover {
                  box-shadow: 0 30px 80px rgba(99,102,241,0.15);
                  transform: translateY(-4px);
                }
                .roadmap-locked-card:hover .locked-glow {
                  opacity: 0.6;
                }
                .locked-glow {
                  position: absolute;
                  top: 50%;
                  left: 50%;
                  transform: translate(-50%, -50%);
                  width: 300px;
                  height: 300px;
                  background: radial-gradient(circle, rgba(124,58,237,0.1) 0%, transparent 70%);
                  pointer-events: none;
                  opacity: 0;
                  transition: opacity 0.5s ease;
                }
              `}</style>
              <div className="locked-glow" />

              {/* Top Badge */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 20px',
                borderRadius: '99px',
                background: 'linear-gradient(135deg, #FDE68A 0%, #F59E0B 100%)',
                color: '#92400E',
                fontSize: '12px',
                fontWeight: 800,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                marginBottom: '32px',
                boxShadow: '0 4px 12px rgba(245,158,11,0.2)',
                transition: 'transform 0.3s ease'
              }} className="gold-badge">
                <Lock size={14} /> Exclusive Future Roadmap
              </div>

              {/* Main Locked Card */}
              <div style={{ marginBottom: '32px' }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '16px 32px',
                  background: '#FFFFFF',
                  border: '1px solid rgba(124,58,237,0.3)',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(99,102,241,0.06)',
                  color: '#4F46E5',
                  fontSize: '18px',
                  fontWeight: 700,
                  cursor: 'default',
                  opacity: 0.9
                }}>
                  <Lock size={20} /> More Future Technologies Coming Soon
                </div>
              </div>

              {/* Exclusive Preview Label */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 16px',
                borderRadius: '99px',
                background: '#F5F3FF',
                color: '#7C3AED',
                fontSize: '13px',
                fontWeight: 700,
                marginBottom: '24px'
              }}>
                <Sparkles size={14} /> Exclusive Preview
              </div>

              {/* Description */}
              <p style={{
                color: '#475569',
                fontSize: '17px',
                lineHeight: '1.8',
                maxWidth: '780px',
                margin: '0 auto 32px'
              }}>
                Additional next-generation technologies are currently under development and will be introduced as part of <span style={{ fontWeight: 700, color: '#4F46E5' }}>The Skill Genie Premium Learning Roadmap</span>.
                These advanced learning modules are designed to help students gain practical exposure to emerging AI, Automation, Enterprise Development, Low-Code, Agentic AI, and Intelligent Platform technologies before they become mainstream industry requirements.
              </p>

              {/* Partner College Callout */}
              <div style={{
                background: '#FFF8E8',
                border: '1px solid #F5D27A',
                borderRadius: '18px',
                padding: '24px',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 16,
                marginBottom: '32px'
              }}>
                <div style={{
                  background: '#F5D27A',
                  color: '#92400E',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Star size={18} fill="currentColor" />
                </div>
                <div>
                  <h4 style={{ margin: '0 0 4px', fontSize: '16px', fontWeight: 800, color: '#92400E' }}>Available first for selected partner colleges.</h4>
                  <p style={{ margin: 0, fontSize: '15px', color: '#B45309', lineHeight: '1.5' }}>
                    Early access to these premium technology tracks will be provided exclusively through The Skill Genie Partner Institution Program as part of our phased innovation rollout.
                  </p>
                </div>
              </div>

              {/* Footer Message */}
              <div style={{ textAlign: 'center' }}>
                <p style={{
                  color: '#6B7280',
                  fontSize: '15px',
                  lineHeight: '1.7',
                  maxWidth: '650px',
                  margin: '0 auto'
                }}>
                  ✨ More cutting-edge AI, Automation, Low-Code, Enterprise, and Data Engineering technologies will be announced soon.
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      <SectionDivider light />

      {/* ══════════════════════════════════════════════════════════
          SECTION 9 — WHY COLLEGES TRUST US
      ══════════════════════════════════════════════════════════ */}

      {/* ══════════════════════════════════════════════════════════
          SECTION 10 — FINAL CTA
      ══════════════════════════════════════════════════════════ */}
      <section className="ip-section-wrapper" style={{ background:'#FAFBFF' }}>
        <div className="ip-container">
          <FadeUp>
            <div style={{
              maxWidth:1200, margin:'0 auto', borderRadius:32, overflow:'hidden',
              background:'var(--primary-gradient)',
              boxShadow:'0 40px 100px rgba(49,46,129,0.25)',
              position:'relative',
            }}>
              {/* Decorative glows */}
              <div style={{ position:'absolute', top:'-60px', right:'-60px', width:300, height:300, borderRadius:'50%', background:'radial-gradient(circle,rgba(255,255,255,.15) 0%,transparent 70%)', pointerEvents:'none' }} />
              <div style={{ position:'absolute', bottom:'-50px', left:'-50px', width:250, height:250, borderRadius:'50%', background:'radial-gradient(circle,rgba(196,181,253,.2) 0%,transparent 70%)', pointerEvents:'none' }} />
              <div style={{ position:'absolute', inset:0, opacity:.05, backgroundImage:'linear-gradient(rgba(255,255,255,.7) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.7) 1px,transparent 1px)', backgroundSize:'40px 40px', pointerEvents:'none' }} />

              <div style={{ position:'relative', zIndex:1, padding:'100px 64px', textAlign:'center' }}>
                <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'8px 20px', borderRadius:'99px', background:'rgba(255,255,255,.15)', border:'1px solid rgba(255,255,255,.25)', fontSize:12, fontWeight:800, color:'#E0E7FF', letterSpacing:'.11em', textTransform:'uppercase', marginBottom:32 }}>
                  <Rocket size={14} /> Institution Partnership
                </div>

                <h2 style={{ color:'#fff', marginBottom: 32 }}>
                  Ready to Transform Your Campus<br />
                  <span className="premium-gradient-text" style={{ background: 'linear-gradient(135deg,#FCD34D,#FDE68A)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>into a Placement-Driven Institution?</span>
                </h2>

                <p style={{ color:'rgba(255,255,255,.85)', maxWidth:700, marginBottom: 56 }}>
                  Schedule a consultation with our Enterprise Team and see exactly how The Skill Genie can improve student outcomes, reduce administrative effort, and strengthen your institution's industry reputation — all from a single AI-powered platform.
                </p>

                {/* CTA buttons */}
                <div style={{ display:'flex', flexWrap:'wrap', gap:24, justifyContent:'center', marginBottom:48 }}>
                  <button
                    onClick={() => navigate('/collegesignup')}
                    style={{
                      display:'inline-flex', alignItems:'center', gap:10,
                      padding:'18px 48px', borderRadius:20, border:'none', cursor:'pointer',
                      fontSize:18, fontWeight:800, color:'#4338CA',
                      background:'#fff',
                      boxShadow:'0 10px 30px rgba(0,0,0,.15)',
                      transition:'all .3s ease',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform='translateY(-5px) scale(1.02)'; e.currentTarget.style.boxShadow='0 20px 50px rgba(0,0,0,.25)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform='translateY(0) scale(1)';   e.currentTarget.style.boxShadow='0 10px 30px rgba(0,0,0,.15)'; }}
                  >
                    <Rocket size={22} /> Request Institutional Demo
                  </button>

                  <button
                    onClick={() => window.open('https://www.theskillgenie.com/contact', '_blank')}
                    style={{
                      display:'inline-flex', alignItems:'center', gap:10,
                      padding:'18px 48px', borderRadius:20, border:'2px solid rgba(255,255,255,.3)', cursor:'pointer',
                      fontSize:18, fontWeight:800, color:'#fff',
                      background:'transparent',
                      transition:'all .3s ease',
                    }}
                    onMouseEnter={e => { 
                      e.currentTarget.style.transform='translateY(-5px) scale(1.02)'; 
                      e.currentTarget.style.background='rgba(255,255,255,.1)';
                      e.currentTarget.style.borderColor='rgba(255,255,255,1)';
                    }}
                    onMouseLeave={e => { 
                      e.currentTarget.style.transform='translateY(0) scale(1)';   
                      e.currentTarget.style.background='transparent';
                      e.currentTarget.style.borderColor='rgba(255,255,255,.3)';
                    }}
                  >
                    <MessageSquare size={22} /> Contact Sales Team
                  </button>
                </div>

                {/* Trust note */}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:12 }}>
                  <div style={{ width:24, height:24, borderRadius:'50%', background:'rgba(255,255,255,.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <span style={{ fontSize:12, color:'rgba(255,255,255,.8)', fontWeight:800 }}>ℹ</span>
                  </div>
                  <p style={{ margin:0, fontSize:14, color:'rgba(255,255,255,.6)', lineHeight:1.6, maxWidth:500, textAlign:'left', marginLeft:0 }}>
                    No commitment required. Our Enterprise Team will walk you through a personalised demo tailored to your institution's size, department structure, and placement goals.
                  </p>
                </div>
              </div>
            </div>
          </FadeUp>

        </div>
      </section>

    </div>
  );
}
